import React from 'react';
import { Alert, Col, Collapse, Divider, Row, Tooltip, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import { Form, FormItem, Input, ResetButton, Select, SubmitButton, Switch } from 'formik-antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import isEmpty from 'lodash/isEmpty';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import { PrivateComponent } from '../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../const/permissions';
import mkTemplateDynamicFormData from '../../utils/mkTemplateDynamicFormData';
import mkMainAndOptionalTemplateFields from '../../../Template builder/utils/mkMainAndOptionalTemplateFields';
import Spinner from '../../../../components/Spinner';
import DynamicFormDataFields from '../../components/DynamicFormDataFields';
import TargetingSettings from '../../../../components/TargetingSettings';



const { Title } = Typography;
const { Panel } = Collapse;


const FormEditCategory = ({
  initialErrors,
  initialValues,
  onSubmit,
  isSubmitting,
  validationSchema,
  templateFields,
  templateOptions,
}) => {
  const intl = useIntl();
  const language = useSelector(({ customise: { language } }) => language);
  const { mainFields, optionalFields } = mkMainAndOptionalTemplateFields(templateFields);

  if (!isEmpty(initialErrors)) {
    window.scrollTo(0, 0);
  }

  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={initialErrors}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        onSubmit(mkTemplateDynamicFormData(values, language, templateFields), resetForm);
      }}
    >
      {({ isValid, dirty, values, setFieldValue, setFieldTouched, resetForm }) => {
        return (
          <Form layout="vertical" >
            <Spinner spinning={isSubmitting}>
              <Title className="hp-mb-24" level={4}>{intl.formatMessage({ id: 'categories-details-title' })}</Title>

              <Row gutter={16}>
                <Col md={12} xs={24}>
                  <Row gutter={16}>
                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-title' })}
                        name='title'
                        required
                      >
                        <Input
                          name="title"
                          placeholder={intl.formatMessage({ id: 'categories-create-form-field-name-placeholder' })}
                        />
                      </FormItem>
                    </Col>
                    <Col span={24}>
                      <FormItem
                        name="client_id"
                        required
                        label={
                          <Row className='w-full' justify="space-between" align="middle">
                            <IntlMessages id="ui-general-app-id" />
                            <Tooltip placement="topRight" title={<IntlMessages id="categories-create-form-field-client-id-help" />}>
                              <InfoCircleOutlined className="hp-ml-8" />
                            </Tooltip>
                          </Row>
                        }
                      >
                        <Input
                          type='number'
                          name='client_id'
                          placeholder={intl.formatMessage({ id: 'ui-general-id-placeholder' })}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-template' })}
                        name='template_id'
                        required
                      >
                        <Select
                          loading={false}
                          allowClear
                          disabled
                          name="template_id"
                          options={templateOptions}
                          placeholder={intl.formatMessage({ id: 'ui-general-choose-template' })}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-description' })}
                        name='description'
                        required
                      >
                        <Input.TextArea
                          rows={3}
                          maxLength={5000}
                          showCount
                          name="description"
                          type="text"
                          placeholder={intl.formatMessage({ id: 'ui-general-specify-description' })}
                        />
                      </FormItem>
                    </Col>

                    <PrivateComponent allowedPermissions={[ Permissions.CONTENT.CATEGORIES.EDIT_OPTIONS ]}>
                      <Col span={24}>
                        <FormItem
                          name='options'
                          label={
                            <Row className='w-full' justify="space-between" align="middle">
                              <IntlMessages id="categories-form-field-options" />
                              <Tooltip placement="topRight" title={<IntlMessages id="categories-form-field-options-help" />}>
                                <InfoCircleOutlined className="hp-ml-8" />
                              </Tooltip>
                            </Row>
                          }
                        >
                          <Input.TextArea
                            rows={5}
                            showCount
                            maxLength={5000}
                            placeholder={intl.formatMessage({ id: 'categories-form-field-options-placeholder' })}
                            name='options'
                          />
                        </FormItem>
                      </Col>
                    </PrivateComponent>
                  </Row>
                </Col>
              </Row>

              {values?.template_id && (
                <>
                  <Divider />
                  <Title className="hp-mb-24" level={4}>
                    <IntlMessages id="ui-general-template-data" />
                  </Title>
                  <Row gutter={[ 16, 16 ]} className="hp-mb-24">
                    <Col span={24}>
                      <DynamicFormDataFields
                        setFieldValue={setFieldValue}
                        fields={mainFields}
                        values={values}
                        span={24}
                      />
                      {!!optionalFields.length && (
                        <Collapse>
                          <Panel
                            key="1"
                            header={
                              <Title level={5} className='collapse-item-title'>
                                <IntlMessages id="ui-general-additional-parameters" />
                              </Title>
                            }
                          >
                            <DynamicFormDataFields
                              setFieldValue={setFieldValue}
                              fields={optionalFields}
                              values={values}
                              span={24}
                            />
                          </Panel>
                        </Collapse>
                      )}
                    </Col>
                  </Row>

                  <Divider />

                  <TargetingSettings
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    values={values}
                  />
                </>
              )}

              <Alert
                message={<IntlMessages id="ui-general-edit-has-changes" />}
                type="info"
                showIcon
              />

              <Row justify='end' className='hp-mt-24 hp-mb-24'>
                <Col>
                  <Row>
                    <IntlMessages id="ui-general-publish-changes" />
                    <Switch name="is_published" className='hp-ml-16' />
                  </Row>
                </Col>
              </Row>

              <Row gutter={[ 16, 16 ]} className='hp-mt-16' justify='end'>
                <Col>
                  <ResetButton
                    disabled={false}
                    onClick={() => resetForm(initialValues)}
                  >
                    <IntlMessages id='ui-general-reset' />
                  </ResetButton>
                </Col>

                <Col>
                  <SubmitButton
                    type="primary"
                    loading={isSubmitting}
                    disabled={!isValid || !dirty}
                  >
                    <IntlMessages id="ui-general-save" />
                  </SubmitButton>
                </Col>
              </Row>
            </Spinner>
          </Form>
        );
      }}
    </Formik>
  );
};

FormEditCategory.propTypes = {
  initialErrors: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  initialValues: PropTypes.object.isRequired,
  templateOptions: PropTypes.array.isRequired,
  templateFields: PropTypes.array.isRequired,
  validationSchema: PropTypes.object.isRequired,
};

export default FormEditCategory;
